import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Spring} from "@khanacademy/wonder-blocks-layout";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingLarge,
    LabelLarge,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {staticUrl} from "@khan/urls";

import type {Badge} from "#profile-package/get-badges.ts";
import {
    getBadgeCategory,
    getBadgeLabel,
} from "#profile-package/profile-tabs.tsx";

const strings = {
    badgeImageAlt: (badgeTypeLabel: string) =>
        t`a large illustration of a ${badgeTypeLabel}`,
} as const;

type Props = {
    badge: Badge;
};

class BadgeSpotlight extends React.Component<Props> {
    render(): React.ReactNode {
        const {badge} = this.props;

        const badgeCategoryName = getBadgeCategory(badge.badgeCategory);
        const badgeLabel = getBadgeLabel(badgeCategoryName, true, false);

        return (
            <View style={styles.badgeBox} testId="badge-spotlight">
                <View style={styles.badgeSpotlight}>
                    <View style={styles.heroBadgeBox}>
                        <View style={styles.heroBadge}>
                            <img
                                src={staticUrl(badge.icons.large)}
                                alt={strings.badgeImageAlt(badgeLabel)}
                            />
                        </View>
                        <View style={styles.heroBadgeDesc}>{badgeLabel}</View>
                    </View>
                    <View style={styles.rightContainer}>
                        <View style={styles.titleContainer}>
                            <HeadingLarge>
                                {badge.translatedDescription}
                            </HeadingLarge>
                        </View>
                        <LabelLarge style={styles.description}>
                            {badge.translatedExtendedDescription}
                        </LabelLarge>
                        <Spring />
                        {badge.isOwned && (
                            <View style={styles.ownershipContainer}>
                                <LabelMedium>
                                    {t`You own this badge.`}
                                </LabelMedium>
                            </View>
                        )}
                    </View>
                </View>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    badgeBox: {
        backgroundColor: color.white,
    },
    badgeSpotlight: {
        backgroundColor: color.offBlack8,

        borderRadius: 5,
        margin: 12,
        paddingTop: 24,
        paddingBottom: 24,
        paddingLeft: 48,
        flexDirection: "row",
        justifyContent: "space-between",
        border: "1px solid #ccc",
        borderBottom: "1px solid #aaa",
        boxShadow: "0 1px 3px #ccc",
    },
    heroBadgeBox: {
        marginRight: 24,
        alignItems: "center",
    },
    heroBadge: {
        maxWidth: 225,
    },
    heroBadgeDesc: {
        color: color.offBlack64,
    },
    rightContainer: {
        marginLeft: 24,
        flexGrow: 1,
        maxWidth: 600,
    },
    titleContainer: {
        backgroundColor: color.white,
        marginRight: -7,
        border: "1px solid #aaa",
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 15,
        paddingRight: 15,
        color: color.offBlack64,
        boxShadow: "0 1px 2px #ccc",

        "::before": {
            content: "''",
            display: "block",
            borderRight: "7px solid transparent",
            borderBottom: "8px solid #ccc",
            width: 0,
            height: 15,
            position: "absolute",
            right: -1,
            top: -24,
        },
    },
    description: {
        marginTop: 12,
        color: color.offBlack64,
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 15,
        paddingRight: 15,
    },
    ownershipContainer: {
        backgroundColor: color.offWhite,
        marginRight: -7,
        border: "1px solid #aaa",
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 15,
        paddingRight: 15,
        color: color.offBlack,
        boxShadow: "0 1px 2px #ccc",
        maxWidth: 300,
        alignSelf: "flex-end",

        "::before": {
            content: "''",
            display: "block",
            borderRight: "7px solid transparent",
            borderBottom: "8px solid #ccc",
            width: 0,
            height: 15,
            position: "absolute",
            right: -1,
            top: -24,
        },
    },
});

export default BadgeSpotlight;
